import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    ApartmentOutlined,
    DeleteOutlined,
    EditOutlined,
    LockOutlined,
    PlusOutlined,
    SettingOutlined,
    SyncOutlined,
    UnlockOutlined,
    UserOutlined,
} from '@ant-design/icons';
import LoginStatusIndicator from '@components/common/elements/LoginStatusIndicator';
import AvatarField from '@components/common/form/AvatarField';
import KitplaySettingModal from '@components/common/form/KitplaySettingModal';
import ListPage from '@components/common/layout/ListPage';
import DragDropTableV2 from '@components/common/table/DragDropTableV2';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE, STATUS_ACTIVE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { printerTypeOptions } from '@constants/masterData';
import useDragDropV2 from '@hooks/useDragDropV2';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { showErrorMessage, showSucsessMessage } from '@services/notifyService';
import { convertUtcToLocalTime, getFullImageUrl, orderNumber, parseSettings } from '@utils';
import { Button, Modal, Table } from 'antd';

import useKitplayDeviceFormModal from './KitplayDeviceFormModal';
import useKitplayPrinterMappingFormModal from './KitplayPrinterMappingFormModal';

import styles from '../index.module.scss';

const messageIntl = defineMessages({
    objectName: 'Device Kitplay',

});

function ExpandedRow({ record, childData, loadingSubItems, handleFetch, mixinFuncs, handleCreateNavigation, editingRowId, editingSettingId }) {
    const childItems = childData[record.printer.id] || [];
    const isLoading = loadingSubItems[record.printer.id] || false;
    const translate = useTranslate();

    const {
        sortedData,
        sortColumn,
        onDragEndDnd,
    } = useDragDropV2({
        data: childItems || [],
        apiConfig: apiConfig.printerMapping.updateSort, 
        indexField: 'ordering',
        onUpdated: () => {
            handleFetch(record);
        },
    });

    const columns = [
        sortColumn,
        {
            dataIndex: 'image',
            key: 'image',
            align: 'center',
            width: 70,
            render: (image) => (
                <div style={{ backgroundColor: '#171717' }}>
                    <AvatarField size="large" icon={<UserOutlined />} src={getFullImageUrl(image)} disablePreview={true} />
                </div>
            ),
        },
        {
            dataIndex: ['printerGroup', 'name'],
            width: 300,
        },
        {
            dataIndex: 'printerNumber',
            render: (data) => (
                <>
                    {translate.formatMessage(commonMessage.printerNumber)} {data}
                </>
            ),
        },
        mixinFuncs.renderActionColumn(
            {
                // addPrinterMapping: mixinFuncs.hasPermission([apiConfig.printerMapping.create.baseURL]),
                editPrinterMapping: mixinFuncs.hasPermission([apiConfig.printerMapping.update.baseURL]),
                deletePrinterMapping: mixinFuncs.hasPermission([apiConfig.printerMapping.delete.baseURL]),
            },
            { width: '120px', className: styles['action-column'], title: '' },
        ),
    ];

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 10, marginBottom: -18, padding: '0 30px' }}>
                <span style={{ fontWeight: 'bold', paddingLeft: 88 }}><FormattedMessage defaultMessage="Kitplay device" /></span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                    <PlusOutlined
                        style={{ color: '#1677ff', cursor: 'pointer' }}
                        onClick={() => {
                            handleCreateNavigation(record);
                        }}
                    />
                    <SyncOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => handleFetch(record)} />
                </div>
            </div>
            <DragDropTableV2
                className={styles['scroll-mapping-container']}
                columns={columns}
                dataSource={sortedData}
                onDragEnd={onDragEndDnd}
                loading={isLoading}
                rowKey={(record) => record.id}
                pagination={false}
                borderColor={true}
                getRowColor={(record) => {
                    const settings = parseSettings(record.settings);
                    return settings?.color;
                }}
                rowClassName={(record) => {
                    return record.id === editingRowId || record.id === editingSettingId ? styles['highlighted-row'] : '';
                }}
            />
        </>
    );
}

function Device({ serverDetail }) {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const { customerId } = useParams();

    const tabKey = searchParams.get('tab');
    const restaurantName = searchParams.get('restaurantName');
    const [loadingSubItems,setLoadingSubItems] = useState({});

    const [childData, setChildData] = useState({});
    const params = new URLSearchParams(location.search);
    const collapseId = params.get('collapseId');
    
    const [fetchedAt, setFetchedAt] = useState(null);

    const [expandedRowKeys, setExpandedRowKeys] = useState(() => {
        return collapseId ? [(collapseId)] : []; 
    });

    const [editingRowId, setEditingRowId] = useState(null);
    const [editingParentRowId, setEditingParentRowId] = useState(null);

    const notification = useNotification();

    const restaurantId = searchParams.get('restaurantId');

    const [settingModalOpen, setSettingModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [editingSettingId, setEditingSettingId] = useState(null);

    const { execute: approveDevice } = useFetch(apiConfig.kitplayDevice.approveDevice);
    const { execute: lockDevice } = useFetch(apiConfig.kitplayDevice.lockDevice);
    const { execute: executeDeletePrinterMapping } = useFetch(apiConfig.printerMapping.delete);

    const handleOpenSetting = (record) => {
        setSelectedRecord(record);
        setSettingModalOpen(true);
        setEditingSettingId(record.id); 
    };

    const handleCloseSetting = () => {
        setSettingModalOpen(false);
        setSelectedRecord(null);
        setEditingSettingId(null);
    };

    const handleSubmit = (record) => {
        const dataUpdate = { id: record.id };
        const action = record.status === STATUS_ACTIVE ? lockDevice : approveDevice;
        action({
            data: dataUpdate,
            onCompleted: () => {
                const messageContent =
                    record.status === STATUS_ACTIVE
                        ? translate.formatMessage(commonMessage.lockSuccess)
                        : translate.formatMessage(commonMessage.approveSuccess);

                mixinFuncs.getList();
                showSucsessMessage(messageContent, translate);
            },
            onError: (error) => {
                const messageContent =
                    record.status === STATUS_ACTIVE
                        ? translate.formatMessage(commonMessage.lockError)
                        : translate.formatMessage(commonMessage.approveError);
                showErrorMessage(messageContent, translate);
            },
        });
    };

    const { data, mixinFuncs, queryFilter, loading, pagination, changePagination, queryParams, pagePath } =
        useListBase({
            apiConfig: apiConfig.printer,
            options: {
                pageSize: DEFAULT_TABLE_ITEM_SIZE,
                objectName: translate.formatMessage(messageIntl.objectName),
            },
            params: {
                restaurantId: restaurantId,
            },
            override: (funcs) => {
                funcs.mappingData = (response) => {
                    if (response.result === true) {
                        setFetchedAt(new Date());
                        return {
                            data: response.data.content,
                            total: response.data.totalElements,
                        };
                    }
                };
                funcs.getList = () => {
                    mixinFuncs.handleFetchList({
                        restaurantId: restaurantId,
                    });
                };
                funcs.getCreateLink = () => {
                    return `${pagePath}/kitplay/group-device/create?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&deviceId=${serverDetail?.id}`;
                };
                funcs.getItemDetailLink = (dataRow) => {
                    return `${pagePath}/kitplay/group-device/${dataRow.id}?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&deviceId=${serverDetail?.id}&collapseId=${dataRow.printer.id}`;
                };

                funcs.additionalActionColumnButtons = () => {
                    const baseButtons = {
                        editChild: (record) => {
                            if (!mixinFuncs.hasPermission([apiConfig.printer.update.baseURL])) return null;
                            return (
                                <BaseTooltip type="edit">
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingParentRowId(record.id);
                                            openEdit(record);
                                        }}
                                    >
                                        <EditOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                    };
                    if (!mixinFuncs.hasPermission([apiConfig.kitplayDevice.approveDevice.baseURL])) return baseButtons;
                    return {
                        ...baseButtons,
                        deleteByPrinterId: (record) => {
                            if (!mixinFuncs.hasPermission([apiConfig.printer.delete.baseURL])) return null;
                            return (
                                <BaseTooltip type="delete">
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            mixinFuncs.showDeleteItemConfirm(record.printer.id);
                                        }}
                                    >
                                        <DeleteOutlined style={{ color: 'red' }} />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        active: (record) => {
                            const Icon = record.status === 1 ? LockOutlined : UnlockOutlined;
                            const iconStyle = record.status === 1 ? { color: 'red' } : {};
                            const title =
                                record.status === 1
                                    ? translate.formatMessage(commonMessage.lockKitplay)
                                    : translate.formatMessage(commonMessage.approveKitplay);
                            return (
                                <BaseTooltip title={title}>
                                    <Button type="link" style={{ padding: 0 }} onClick={() => handleSubmit(record)}>
                                        <Icon style={iconStyle} />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        loginStatus: (record) => {
                            return <LoginStatusIndicator lastTimeLogin={record.lastTimeLogin} fetchedAt={fetchedAt} translate={translate} />;
                        },
                        setting: (record) => {
                            return (
                                <BaseTooltip title={translate.formatMessage(commonMessage.editSetting)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenSetting(record);
                                        }}
                                    >
                                        <SettingOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        mapping: (record) => {
                            return (
                                <BaseTooltip title={translate.formatMessage(commonMessage.mapping)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={() => {
                                            navigate(
                                                `${generatePath(routes.resPrinterMappingDevicesRestaurantListPage.path)}?tab=kitplay&restaurantId=${restaurantId}&restaurantName=${restaurantName}&printerId=${record.printer.id}&deviceId=${serverDetail?.id}&collapseId=${record.printer.id}`,
                                            );
                                        }}
                                    >
                                        <ApartmentOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        addPrinterMapping: (record) => {
                            const currentOrdering = record?.ordering !== undefined ? Number(record.ordering) + 1 : '';
                            return (
                                <BaseTooltip type="add" objectName={translate.formatMessage(commonMessage.mapping)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={() => {
                                            openMappingCreate(record, currentOrdering);
                                        }}
                                    >
                                        <PlusOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        editPrinterMapping: (record) => {
                            return (
                                <BaseTooltip type="edit" objectName={translate.formatMessage(commonMessage.mapping)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={() => {
                                            // openMappingEdit(record);
                                            handleOpenMappingEdit(record);
                                        }}
                                    >
                                        <EditOutlined />
                                    </Button>
                                </BaseTooltip>
                            );
                        },
                        deletePrinterMapping: (record) => {
                            return (
                                <BaseTooltip type="delete" objectName={translate.formatMessage(commonMessage.mapping)}>
                                    <Button
                                        type="link"
                                        style={{ padding: 0 }}
                                        onClick={() => {
                                            Modal.confirm({
                                                title: translate.formatMessage(commonMessage.deleteTitle, { objectName: translate.formatMessage(commonMessage.mapping) }),
                                                content: '',
                                                okText: translate.formatMessage(commonMessage.ok),
                                                cancelText: translate.formatMessage(commonMessage.cancel),
                                                centered: true,
                                                onOk: () => {
                                                    executeDeletePrinterMapping({
                                                        pathParams: { id: record.id },
                                                        onCompleted: () => {
                                                            showSucsessMessage(translate.formatMessage(commonMessage.deleteSuccess, { objectName: translate.formatMessage(commonMessage.mapping) }), translate);
                                                            handleFetch(record);
                                                        },
                                                        onError: () => {
                                                            showErrorMessage(translate.formatMessage(commonMessage.error), translate);
                                                        },
                                                    });
                                                },
                                            });
                                        }}
                                    >
                                        <DeleteOutlined style={{ color: 'red' }} />
                                    </Button>
                                </BaseTooltip>
                            );
                        },  
                    };
                };
            },
        });

    const { openCreate, openEdit, renderModals: renderDeviceModals } = useKitplayDeviceFormModal({
        restaurantId,
        deviceId: serverDetail?.id,
        onReload: () => {
            mixinFuncs.getList(),
            setEditingRowId(null);
        },
        onClose: () => setEditingParentRowId(null),
    });

    const { openMappingCreate, openMappingEdit, renderModals: renderMappingModals } = useKitplayPrinterMappingFormModal({
        deviceId: serverDetail?.id,
        // onReloadRow: (record) => handleFetch(record),
        onReloadRow: (record) => {
            handleFetch(record);
            setEditingRowId(null);
        },
        onClose: () => setEditingRowId(null),
    });

    const handleOpenMappingEdit = (record) => {
        setEditingRowId(record.id);
        openMappingEdit(record);
    };

    const printerTypeValues = translate.formatKeys(printerTypeOptions, ['label']);

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'id',
            render: (text, record, index) => {
                return orderNumber(pagination, index);
            },
            width: 50,
        },
        {
            title: <FormattedMessage defaultMessage="Device ID" />,
            dataIndex: 'deviceId',
            width: 120,
        },
        {
            title: <FormattedMessage defaultMessage="Device name" />,
            dataIndex: 'name',
            key: 'name',
        },
        // {
        //     title: <FormattedMessage defaultMessage="Number of columns" />,
        //     dataIndex: ['printer', 'numberOfColumns'],
        //     align: 'center',
        //     width: 180,
        // },
        {
            title: <FormattedMessage defaultMessage="Area" />,
            dataIndex: ['printer', 'area', 'name'],
            width: 200,
        },
        {
            title: translate.formatMessage(commonMessage.printerType),
            dataIndex: ['printer', 'type'],
            width: 140,
            render: (type) => printerTypeValues.find((item) => item.value === type)?.label,
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: 'expireDate',
            align: 'right',
            render: (expireDate) => {
                const expireDateLocal = convertUtcToLocalTime(expireDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{expireDateLocal}</div>;
            },
        },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                setting: { ignorePermission: true },
                editChild: mixinFuncs.hasPermission([apiConfig.printer.update.baseURL]),
                deleteByPrinterId: mixinFuncs.hasPermission([apiConfig.printer.delete.baseURL]),
                loginStatus: true,
            },
            { width: '120px', className: styles['action-column'] },
        ),
    ].filter(Boolean);

    const { execute: fetchSubItems } = useFetch(apiConfig.printerMapping.getList, {
        immediate: false,
        mappingData: (response) => {
            if (response.result === true) {
                return {
                    data: response.data.content,
                    total: response.data.totalElements,
                };
            } else {
                console.error('Error: Invalid response format', response);
            }
        },
    });

    const handleFetch = (record) => {
        const params = { printerId: record.printer.id };
        setLoadingSubItems(prev => ({
            ...prev,
            [record.printer.id]: true,
        }));
        fetchSubItems({
            params,
            onCompleted: (response) => {
                if (response && response.data) {
                    if (response.data.content) {
                        const filteredData = response.data.content?.map((child) => {
                            return {
                                ...child,
                                printer: record.printer,
                            };
                        });
                        setChildData((prevData) => ({
                            ...prevData,
                            [record.printer.id]: filteredData,
                        }));
                    } else {
                        setChildData((prevData) => ({
                            ...prevData,
                            [record.printer.id]: [],
                        }));
                    }
                } else {
                    showErrorMessage('Error', translate);
                }
                setLoadingSubItems(prev => ({
                    ...prev,
                    [record.printer.id]: false,
                }));
            },
            onError: (error) => {
                const messageContent = 'Error';
                showErrorMessage(messageContent, translate);
                setLoadingSubItems(prev => ({
                    ...prev,
                    [record.printer.id]: false,
                }));
            },
        });
    };

    useEffect(() => {
        if (collapseId && data && data.length > 0) {
            const activeRecord = data.find(item => String(item?.printer.id).trim() === collapseId.trim());
            if (activeRecord) {
                if (!childData[activeRecord.id]) {
                    handleFetch(activeRecord);
                }
                setExpandedRowKeys([activeRecord.id]);
            }
        }
    }, [collapseId, data]);

    const handleExpand = (expanded, record) => {
        if (expanded) {
            if (!childData[record.id]) {
                handleFetch(record);
            }
            setExpandedRowKeys((prevKeys) => [...prevKeys, record.id]);
        } else {
            setExpandedRowKeys((prevKeys) => prevKeys.filter((id) => id !== record.id));
        }
    };
    

    const handleCreateNavigation = (record) => {
        const currentChildItems = childData[record?.printer?.id] || [];
        const currentOrdering = currentChildItems.length;
        openMappingCreate(record, currentOrdering);
    };

    const expandedRowRender = (record) => (
        <ExpandedRow 
            record={record}
            childData={childData}
            loadingSubItems={loadingSubItems}
            handleFetch={handleFetch}
            mixinFuncs={mixinFuncs}
            handleCreateNavigation={handleCreateNavigation}
            editingRowId={editingRowId}
        />
    );

    return (
        <>
            <ListPage
                actionBar={
                    mixinFuncs.hasPermission([apiConfig.printer.create.baseURL]) && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>
                            </Button>
                        </div>
                    )
                }
                baseTable={
                    <BaseTable
                        onChange={changePagination}
                        pagination={pagination}
                        loading={loading}
                        dataSource={data}
                        columns={columns}
                        expandable={{
                            expandedRowRender,
                            expandedRowKeys: expandedRowKeys,
                            defaultExpandedRowKeys: ['0'],
                            onExpand: handleExpand,
                        }}
                        onRow={(record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                            },
                            className: record.id === editingParentRowId || record.id === editingSettingId ? styles['highlighted-row'] : '',
                        })}
                    />
                }
            />
            {renderDeviceModals()}
            {renderMappingModals()}
            <KitplaySettingModal
                open={settingModalOpen}
                record={selectedRecord}
                onClose={handleCloseSetting}
                onSuccess={() => mixinFuncs.getList()}
            />
        </>
    );
}
export default Device;
